import { AfterViewInit, Component, Input, NgZone, OnDestroy, OnInit } from '@angular/core';
// amCharts imports
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import { BaseChartService } from '../../../../shared/services/base-chart.service';
import { BarChartData } from '../interfaces/bar-chart-data.interface';
import { SpinnerService } from '../../../../shared/services/spinner.service';
import { CommonService } from '../../../../shared/services/common.service';
import { Observable, of } from 'rxjs';

/**
 * Step Line Graph Component
 * @author: PRachethan | 02-08-2025 
 */
@Component({
  selector: 'bi-step-line-graph',
  standalone: false,
  templateUrl: './step-line-graph.component.html',
  styleUrl: './step-line-graph.component.scss'
})
export class StepLineGraphComponent implements OnInit, AfterViewInit, OnDestroy {
  private root!: am5.Root;
  data: BarChartData[] = [];
  private stepLineGraphReportSubscription: any;
  private isStepLineViewInitialized = false;
  private isCreatingChart = false;

  @Input() reportData: any = null;

  constructor(
    private zone: NgZone,
    private _baseChartService: BaseChartService,
    private _spinnerService: SpinnerService,
    private _commonService: CommonService
  ) { }

  ngOnInit(): void {
    this.zone.runOutsideAngular(() => {
      this.stepLineGraphReportSubscription = this._commonService.graphReportState.subscribe((data: any) => {
        if (data && data.reportData && Array.isArray(data.reportData) && data.reportData.length > 0) {
          this.reportData = data;
          console.log("Step Line Graph Data", this.reportData);
          this.data = this._baseChartService.transformReportData(data);
          if (this.isStepLineViewInitialized) {
            this.updateChart(this.data);
          }
        } else {
        }
      });
    });
  }

  ngAfterViewInit(): void {
    this.isStepLineViewInitialized = true;
    if (this.reportData && this.reportData.reportData && Array.isArray(this.reportData.reportData) && this.reportData.reportData.length > 0) {
      this.zone.runOutsideAngular(() => {
        // Check if DOM element exists before creating chart
        const chartContainer = document.getElementById('step-line-chartdiv');
        if (chartContainer) {
          this.data = this._baseChartService.transformReportData(this.reportData);
          this.updateChart(this.data);
        } else {
          console.log('Step Line Chart - DOM element not ready in ngAfterViewInit, will retry later');
        }
      });
    }
  }

  ngOnChanges(): void {
    if (this.reportData && this.reportData.reportData && Array.isArray(this.reportData.reportData) && this.reportData.reportData.length > 0 && this.isStepLineViewInitialized) {
      this.zone.runOutsideAngular(() => {
        // Check if DOM element exists before creating chart
        const chartContainer = document.getElementById('step-line-chartdiv');
        if (chartContainer) {
          this.data = this._baseChartService.transformReportData(this.reportData);
          this.updateChart(this.data);
        } else {
          console.log('Step Line Chart - DOM element not ready in ngOnChanges, will retry later');
          // Retry after a short delay
          setTimeout(() => {
            const retryContainer = document.getElementById('step-line-chartdiv');
            if (retryContainer) {
              this.data = this._baseChartService.transformReportData(this.reportData);
              this.updateChart(this.data);
            }
          }, 100);
        }
      });
    }
  }

  createChart(data: BarChartData[]) {
    // Check if the chart container exists
    const chartContainer = document.getElementById('step-line-chartdiv');
    if (!chartContainer) {
      console.error('Step Line Chart - Chart container not found! Make sure element with id "step-line-chartdiv" exists');
      return;
    }

    // Dispose existing chart before creating new one
    if (this.root) {
      console.log('Step Line Chart - Disposing existing chart...');
      this._baseChartService.disposeChart(this.root);
      this.root = undefined as any;
    }

    try {
      console.log('Step Line Chart - Creating new chart...');
      this.root = this._baseChartService.createRoot('step-line-chartdiv');
      this._baseChartService.attachExporting(this.root, { 
        filePrefix: 'step-line',
        data: data
      });

      const categoryField = this._baseChartService.getCategoryField(data);
      const valueFields = this._baseChartService.getValueFields(data);

      const chart = this._baseChartService.createChartContainer(this.root, {
        panX: true,
        panY: false,
        wheelX: 'none',
        wheelY: 'none',
        layout: this.root.verticalLayout
      });

      this._baseChartService.setupColorPalette(chart);

      const categoryAxis = this._baseChartService.createCategoryAxis(chart, categoryField, {
        axisType: 'x',
        minGridDistance: 20,
        cellStartLocation: 0.1,
        cellEndLocation: 0.9,
        rotation: 45
      });

      const valueAxis = this._baseChartService.createValueAxis(chart, {
        axisType: 'y'
      });

      // Set data
      categoryAxis.data.setAll(data);

      // Create series for each value field
      valueFields.forEach((valueField, index) => {
        let series = chart.series.push(
          am5xy.StepLineSeries.new(this.root, {
            xAxis: categoryAxis,
            yAxis: valueAxis,
            valueYField: valueField,
            categoryXField: categoryField,
            name: valueField,
            visible: true
          })
        );

        // Set stroke width like legacy implementation
        series.strokes.template.set('strokeWidth', 3);

        // Add bullets like legacy implementation
        series.bullets.push((root, series, dataItem) => {
          return am5.Bullet.new(root, {
            sprite: am5.Circle.new(root, {
              radius: 5,
              fill: series.get('fill')
            })
          });
        });

        // Tooltip background matches the hovered step line color
        const tooltip = am5.Tooltip.new(this.root, {
          getFillFromSprite: true,
          labelText: `{name}: {valueY.formatNumber("#,##0")}\n{categoryX}`,
          pointerOrientation: 'horizontal'
        });
        tooltip.label.set('fill', am5.color(0xFFFFFF));
        this._baseChartService.setupTooltipAnimation(tooltip);
        series.set('tooltip', tooltip);

        // Set data
        series.data.setAll(data);
      });

      // Add cursor for hover + tooltip
      const cursor = chart.set('cursor', am5xy.XYCursor.new(this.root, {
        xAxis: categoryAxis,
        yAxis: valueAxis
      }));
      cursor.lineY.set('visible', false);
      cursor.set('snapToSeries', chart.series.values);

      // Add legend
      let legend = chart.children.push(
        this._baseChartService.createLegend(this.root)
      );

      // Enhanced legend animation
      this._baseChartService.setupLegendAnimation(legend);

      // Configure legend
      legend.set('maxHeight', 40);

      // Configure legend markers
      let markerTemplate = legend.markers.template;
      markerTemplate.set('width', 12);
      markerTemplate.set('height', 12);

      // Set legend data
      legend.data.setAll(chart.series.values);

      console.log('Step Line Chart - Chart created successfully');

    } catch (error) {
      console.error('Error creating step line chart:', error);
    }
  }

  private updateChart(data: BarChartData[]) {
    // Prevent multiple chart updates
    if (this.isCreatingChart) {
      console.log('Step Line Chart - Chart update skipped, chart creation in progress...');
      return;
    }

    // Check if DOM element exists before proceeding
    const chartContainer = document.getElementById('step-line-chartdiv');
    if (!chartContainer) {
      console.log('Step Line Chart - DOM element not ready in updateChart, will retry later');
      // Retry after a short delay
      setTimeout(() => {
        this.updateChart(data);
      }, 100);
      return;
    }

    // Dispose existing chart first
    if (this.root) {
      console.log('Step Line Chart - Disposing chart in updateChart...');
      this._baseChartService.disposeChart(this.root);
      this.root = undefined as any;
    }
    
    // Add a longer delay to ensure proper disposal and DOM element availability
    setTimeout(() => {
      this.createChart(data);
    }, 200);
    
    // Update the chart data in the registry
    BaseChartService.updateActiveChartData(data);
  }

  ngOnDestroy(): void {
    // Hide spinner if it's still showing
    this._spinnerService.hide();
    this._baseChartService.disposeChart(this.root);
    this.stepLineGraphReportSubscription.unsubscribe();
  }
}
